import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { reportsService } from "../../services/reports-service.js";
import { REPORT_LEVELS, REPORT_TYPES, REPORT_VARIANT_TYPES, REPORT_LEVEL_MODE } from "../../utils/sodReports.js";
import { BUSY_TYPES } from "../../utils/busy.js";

const initialState = {
    roles: [],
    groups: [],
    systems: [],
    extendedSystems: [],
    matrixHeaders: [],
    risks: [],
    riskLevels: [],
    parametersOpen: true,

    canFetchResults: false,
    resultsOpen: false,
    reportLevel: REPORT_LEVELS.OPERATIONS,
    reportType: REPORT_TYPES.FINAL,
    reportLevelMode: REPORT_LEVEL_MODE.ACTION_PERMISSION,
    isReportCalculated: false,
    rows: [],
    columns: [],
    total: 0,
    selectedIds: [],
    searchString: "",
    pageNumber: 1,
    limitNumber: 10,
    sortData: [],
    filterData: [],
    busy: false,
    busyType: null,
    error: null,
    validationErrors: null,

    variantCreated: false,
    variants: [],
    currentVariant: null,
    standardParameters: {
        roles: [],
        systems: [],
        matrixHeaders: [],
        risks: [],
        riskLevels: [],
        extendedSystems: [],
        reportLevelMode: REPORT_LEVEL_MODE.ACTION_PERMISSION,
    },
};

export const fetchReportColumns = createAsyncThunk("reports/roleLevel/fetchReportColumns", async (_, { getState }) => {
    const state = getState();
    const columns = await reportsService.getRoleLevelReportColumns(state.reports.roleLevel.reportLevel, state.reports.roleLevel.reportType);
    return columns;
});

export const fetchReportResults = createAsyncThunk("reports/roleLevel/fetchReportResults", async ({ params }, { getState }) => {
    const state = getState();

    if (!state.reports.roleLevel.canFetchResults) {
        return;
    }

    const newParams = {
        ...params,
        report_level: state.reports.roleLevel.reportLevel,
        report_type: state.reports.roleLevel.reportType,
    };

    const response = await reportsService.getRoleLevelReportResults(newParams);
    return response;
});

export const calcReport = createAsyncThunk("reports/roleLevel//calcReport", async (_, { getState, rejectWithValue }) => {
    const state = getState();

    const systemsForCalc = state.reports.roleLevel.extendedSystems.filter((item) => !item.isGroup).map((item) => item.key);
    const groupForCalc = state.reports.roleLevel.extendedSystems.filter((item) => item.isGroup).map((item) => item.key);
    const reportLevelMode = state.reports.roleLevel.reportLevelMode;
    const newParams = {
        report_level_mode: reportLevelMode,
    };

    const form = {
        roles: state.reports.roleLevel.roles,
        systems: systemsForCalc,
        groups: groupForCalc,
        matrix_headers: state.reports.roleLevel.matrixHeaders.map((item) => item.key),
        risks: state.reports.roleLevel.risks.map((item) => item.key),
        risk_levels: state.reports.roleLevel.riskLevels.map((item) => item.key),
    };

    try {
        await reportsService.calRoleLevelReport(form, newParams);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const saveVariant = createAsyncThunk("reports/roleLevel/saveVariant", async (variantName, { getState, rejectWithValue }) => {
    const state = getState();

    const variant = state.reports.roleLevel.variants.find((variant) => variant.name === variantName);
    const currentVariant = state.reports.roleLevel.currentVariant;
    const sourceVariantId = currentVariant && currentVariant.id === variant?.id ? null : currentVariant?.id;
    const isVariantNew = !variant;

    const systemsForCalc = state.reports.roleLevel.extendedSystems.filter((item) => !item.isGroup).map((item) => item.key);
    const groupForCalc = state.reports.roleLevel.extendedSystems.filter((item) => item.isGroup).map((item) => item.key);

    const variantData = {
        roles: state.reports.roleLevel.roles,
        systems: systemsForCalc,
        matrix_headers: state.reports.roleLevel.matrixHeaders.map((item) => item.key),
        risks: state.reports.roleLevel.risks.map((item) => item.key),
        risk_levels: state.reports.roleLevel.riskLevels.map((item) => item.key),
        groups: groupForCalc,
        report_level_mode: state.reports.roleLevel.reportLevelMode,
    };

    const requestData = {
        id: variant ? variant.id : null,
        variant_name: variantName,
        variant_data: variantData,
        source_variant_id: sourceVariantId || null,
        variant_type: REPORT_VARIANT_TYPES.ROLES,
    };

    try {
        return await reportsService.saveVariant(requestData, isVariantNew);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const deleteVariant = createAsyncThunk("reports/roleLevel/deleteVariant", async (variantName, { rejectWithValue, getState }) => {
    const state = getState();
    const variant = state.reports.roleLevel.variants.find((variant) => variant.name === variantName);

    try {
        return await reportsService.deleteVariant(variant.id);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const setFavoritesForVariant = createAsyncThunk(
    "reports/roleLevel/setFavoritesForVariant",
    async ({ variant, inFavorites }, { rejectWithValue }) => {
        const form = {
            variant_name: variant.name,
            variant_type: REPORT_VARIANT_TYPES.ROLES,
            in_favorites: inFavorites,
            variant_id: variant.id,
        };

        try {
            await reportsService.setFavoritesForVariant(form);

            return { variantName: variant.name, inFavorites: inFavorites };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const fetchVariantsByUser = createAsyncThunk("reports/roleLevel/fetchVariantsByUser", async (searchString) => {
    const params = {
        variant_type: REPORT_VARIANT_TYPES.ROLES,
    };

    if (searchString) {
        params.search = searchString;
    }

    const response = await reportsService.getVariantsByUser(params);

    return response;
});

export const exportReport = createAsyncThunk("reports/roleLevel/exportReport", async (_, { getState, rejectWithValue }) => {
    const state = getState();

    const params = {
        report_level: state.reports.roleLevel.reportLevel,
        report_type: state.reports.roleLevel.reportType,
    };

    try {
        await reportsService.exportRoleLevelReport(params);
    } catch (error) {
        rejectWithValue(error);
    }
});

export const compareReport = createAsyncThunk("reports/roleLevel//compareReport", async (_, { getState, rejectWithValue }) => {
    const state = getState();

    const systemsForCalc = state.reports.roleLevel.extendedSystems.filter((item) => !item.isGroup).map((item) => item.key);
    const groupForCalc = state.reports.roleLevel.extendedSystems.filter((item) => item.isGroup).map((item) => item.key);

    const form = {
        roles: state.reports.roleLevel.roles,
        systems: systemsForCalc,
        matrix_headers: state.reports.roleLevel.matrixHeaders.map((item) => item.key),
        groups: groupForCalc,
        risks: state.reports.roleLevel.risks.map((item) => item.key),
        risk_levels: state.reports.roleLevel.riskLevels.map((item) => item.key),
    };

    try {
        await reportsService.compareRoleLevelReport(form);
    } catch (error) {
        return rejectWithValue(error);
    }
});

export const setReportLevelWithColumns = (reportLevel) => (dispatch) => {
    dispatch(setReportLevel(reportLevel));
    dispatch(fetchReportColumns());
};

export const setReportTypeWithColumns = (reportType) => (dispatch) => {
    dispatch(setReportType(reportType));
    dispatch(fetchReportColumns());
};

export const saveReportColumns = createAsyncThunk("reports/roleLevel/saveReportColumns", async ({ columns, reportLevel, reportType }) => {
    await reportsService.saveRoleLevelReportColumns(columns, reportLevel, reportType);
    return { columns, reportLevel, reportType };
});

const roleLevelSlice = createSlice({
    name: "roleLevel",
    initialState: initialState,
    reducers: {
        setRoles(state, action) {
            state.roles = action.payload;
        },

        setSystems(state, action) {
            state.systems = action.payload;
        },

        setSystemsExtended(state, action) {
            state.extendedSystems = action.payload;
        },

        setMatrixHeaders(state, action) {
            state.matrixHeaders = action.payload;
        },

        setRisks(state, action) {
            state.risks = action.payload;
        },

        setRiskLevels(state, action) {
            state.riskLevels = action.payload;
        },

        setParametersOpen(state, action) {
            state.parametersOpen = action.payload;
        },

        setResultsOpen(state, action) {
            state.resultsOpen = action.payload;
        },

        setReportLevel(state, action) {
            state.pageNumber = 1;
            state.searchString = "";
            state.filterData = [];
            state.sortData = [];
            state.reportLevel = action.payload;
        },

        setReportType(state, action) {
            state.pageNumber = 1;
            state.searchString = "";
            state.filterData = [];
            state.sortData = [];
            state.reportType = action.payload;
        },
        setReportLevelMode(state, action) {
            state.reportLevelMode = action.payload;
        },

        setCanFetchResults(state, action) {
            state.canFetchResults = action.payload;
        },

        setEmptyResultTable(state) {
            state.total = 0;
            state.rows = [];
        },

        setError(state, action) {
            state.error = action.payload;
        },

        setSelectedIds(state, action) {
            state.selectedIds = action.payload;
        },

        setSearchString(state, action) {
            if (state.searchString !== action.payload) {
                state.pageNumber = 1;
                state.searchString = action.payload;
            }
        },

        setPageNumber(state, action) {
            state.pageNumber = action.payload;
        },

        setLimitNumber(state, action) {
            state.pageNumber = 1;
            state.limitNumber = action.payload;
        },

        setSortData(state, action) {
            state.pageNumber = 1;
            state.sortData = action.payload;
        },

        setFilterData(state, action) {
            state.pageNumber = 1;
            state.filterData = action.payload;
        },

        setCurrentVariant(state, action) {
            if (!state.currentVariant) {
                state.standardParameters.roles = state.roles;
                state.standardParameters.systems = state.systems;
                state.standardParameters.extendedSystems = state.extendedSystems;
                state.standardParameters.matrixHeaders = state.matrixHeaders;
                state.standardParameters.risks = state.risks;
                state.standardParameters.riskLevels = state.riskLevels;
            }

            const variant = action.payload;

            const systemsCurrent = variant ? variant.data.systems : state.standardParameters.extendedSystems.filter((item) => !item.isGroup);
            const groupsCurrent = variant ? variant.data.groups : state.standardParameters.extendedSystems.filter((item) => item.isGroup);

            const systemPart = systemsCurrent.map((systemInfo) => ({
                key: systemInfo.key,
                text: systemInfo.text,
                isGroup: false,
            }));

            const groupPart = groupsCurrent.map((groupInfo) => ({
                key: groupInfo.key,
                text: groupInfo.text,
                isGroup: true,
            }));

            state.currentVariant = variant;

            state.roles = variant ? variant.data.roles : state.standardParameters.roles;

            state.systems = variant ? variant.data.systems : state.standardParameters.systems;

            state.extendedSystems = [...systemPart, ...groupPart];

            state.matrixHeaders = variant ? variant.data.matrixHeaders : state.standardParameters.matrixHeaders;

            state.risks = variant ? variant.data.risks : state.standardParameters.risks;

            state.riskLevels = variant ? variant.data.riskLevels : state.standardParameters.riskLevels;
            state.reportLevelMode = variant ? variant.data.reportLevelMode : state.standardParameters.reportLevelMode;

            variant?.data.reportLevelMode == REPORT_LEVEL_MODE.CRITICAL_OBJECTS
                ? (state.reportLevel = REPORT_LEVELS.CRITICAL_OBJECTS)
                : (state.reportLevel = REPORT_LEVELS.OPERATIONS);
        },

        clearFilters(state) {
            state.selectedIds = [];
            state.searchString = "";
            state.pageNumber = 1;
            state.sortData = [];
            state.filterData = [];
        },
        clearParameters(state) {
            state.roles = [];
            state.groups = [];
            state.systems = [];
            state.extendedSystems = [];
            state.matrixHeaders = [];
            state.risks = [];
            state.riskLevels = [];
            state.parametersOpen = true;
        },
        clearReport(state) {
            const initialReportData = {
                canFetchResults: false,
                resultsOpen: false,
                reportLevel: REPORT_LEVELS.OPERATIONS,
                reportType: REPORT_TYPES.FINAL,
                rows: [],
                columns: [],
                total: 0,
                selectedIds: [],
                searchString: "",
                pageNumber: 1,
                limitNumber: 10,
                sortData: [],
                filterData: [],
            };
            for (const resultsKey in initialReportData) {
                state[resultsKey] = initialReportData[resultsKey];
            }
        },
        clearVariant(state) {
            state.currentVariant = null;
        },
    },
    extraReducers: {
        [fetchReportResults.pending]: (state) => {
            state.busy = true;
            state.busyType = BUSY_TYPES.LOAD;
            state.rows = [];
        },

        [fetchReportResults.rejected]: (state, action) => {
            const error = action.payload;

            state.busy = false;
            state.error = error;

            if (error && error.code === "validation") {
                state.validationErrors = error.errors;
            }
        },

        [fetchReportResults.fulfilled]: (state, action) => {
            state.busy = false;
            state.error = null;
            state.validationErrors = null;

            if (action.payload) {
                state.resultsOpen = true;
                state.rows = action.payload.rows;
                state.total = action.payload.total;
            }
        },

        [calcReport.pending]: (state) => {
            state.busy = true;
            state.busyType = BUSY_TYPES.LOAD;
            state.error = null;
            state.validationErrors = null;
            state.isReportCalculated = false;
        },

        [calcReport.rejected]: (state, action) => {
            state.busy = false;
            const error = action.payload;

            state.busy = false;
            state.error = error;

            if (error && error.code === "validation") {
                state.validationErrors = error.errors;
            }
        },

        [calcReport.fulfilled]: (state) => {
            state.selectedIds = [];
            state.searchString = "";
            state.pageNumber = 1;
            state.sortData = [];
            state.filterData = [];
            state.canFetchResults = true;
            state.isReportCalculated = true;
        },

        [fetchReportColumns.pending]: (state) => {
            state.busy = true;
            state.busyType = BUSY_TYPES.LOAD;
        },

        [fetchReportColumns.fulfilled]: (state, action) => {
            state.busy = false;
            state.columns = action.payload?.filter((column) => column.active);
        },

        [fetchReportColumns.rejected]: (state, action) => {
            state.busy = false;
            state.error = action.error;
        },

        [saveVariant.pending]: (state) => {
            state.variantCreated = false;
            state.busy = true;
            state.busyType = BUSY_TYPES.SAVE;
        },

        [saveVariant.fulfilled]: (state, action) => {
            state.busy = false;
            state.validationErrors = [];
            state.variantCreated = true;

            const savedVariant = action.payload;
            const index = state.variants.findIndex((variant) => variant.name === savedVariant.name);

            if (index === -1) {
                state.variants.push(savedVariant);
            } else {
                state.variants[index] = savedVariant;
            }

            if (state.currentVariant?.name === savedVariant.name) {
                state.currentVariant = savedVariant;
            }
        },

        [saveVariant.rejected]: (state, action) => {
            const error = action.payload;

            state.busy = false;
            state.error = error;

            if (error && error.code === "validation") {
                state.validationErrors = error.errors;
            }
        },

        [deleteVariant.pending]: (state) => {
            state.busy = true;
            state.busyType = BUSY_TYPES.SAVE;
        },

        [deleteVariant.fulfilled]: (state, action) => {
            const variantName = action.meta.arg;

            state.busy = false;
            state.variants = state.variants.filter((variant) => variant.name !== variantName);

            if (state.currentVariant?.name === variantName) {
                state.currentVariant = null;
                state.roles = [];
                state.systems = [];
                state.extendedSystems = [];
                state.matrixHeaders = [];
                state.risks = [];
                state.riskLevels = [];
            }
        },

        [deleteVariant.rejected]: (state, action) => {
            state.busy = false;
            state.error = action.payload;
        },

        [setFavoritesForVariant.pending]: (state) => {
            state.busy = true;
            state.busyType = BUSY_TYPES.SAVE;
        },

        [setFavoritesForVariant.fulfilled]: (state, action) => {
            const { variantName, inFavorites } = action.payload;

            state.busy = false;

            const variant = state.variants.find((variant) => variant.name === variantName);

            if (variant) {
                variant.inFavorites = inFavorites;
            }

            if (state.currentVariant?.id === variant?.id) {
                state.currentVariant.inFavorites = inFavorites;
            }
        },

        [setFavoritesForVariant.rejected]: (state, action) => {
            state.busy = false;
            state.error = action.payload;
        },

        [fetchVariantsByUser.fulfilled]: (state, action) => {
            state.variants = action.payload;
        },

        [fetchVariantsByUser.rejected]: (state, action) => {
            state.error = action.error;
        },

        [exportReport.rejected]: (state, action) => {
            state.error = action.error;
        },

        [compareReport.pending]: (state) => {
            state.busy = true;
            state.busyType = BUSY_TYPES.LOAD;
            state.error = null;
            state.validationErrors = null;
        },

        [compareReport.rejected]: (state, action) => {
            const error = action.payload;

            state.busy = false;
            state.error = error;

            if (error.code === "validation") {
                state.validationErrors = error.errors;
            }
        },

        [compareReport.fulfilled]: (state) => {
            state.busy = false;
            state.error = null;

            state.selectedIds = [];
            state.searchString = "";
            state.pageNumber = 1;
            state.sortData = [];
            state.filterData = [];
        },

        [saveReportColumns.fulfilled]: (state, action) => {
            const { columns, reportLevel, reportType } = action.payload;

            if (state.reportLevel === reportLevel && state.reportType === reportType) {
                state.columns = columns.filter((column) => column.active);
            }
        },
    },
});

export const {
    setRoles,
    setSystems,
    setMatrixHeaders,
    setRisks,
    setSystemsExtended,
    setRiskLevels,
    setParametersOpen,
    setResultsOpen,
    setReportLevel,
    setReportType,
    setError,
    setSelectedIds,
    setSearchString,
    setPageNumber,
    setLimitNumber,
    setSortData,
    setFilterData,
    setCurrentVariant,
    clearFilters,
    setReportLevelMode,
    setCanFetchResults,
    setEmptyResultTable,
    clearParameters,
    clearReport,
    clearVariant
} = roleLevelSlice.actions;

export default roleLevelSlice.reducer;
